import { AdminUserRoles, Gender, UserRole } from '../enums';

export interface AcademicInfo {
  grade?: string;
  division?: string;
  subjects?: string[];
  section?: string;
  rollNo?: string;
}

export interface TeacherClassInfo extends AcademicInfo {
  isClassTeacher?: boolean;
}

export interface CourseInfo extends AcademicInfo {
  academicYear: {
    from: Date;
    to: Date;
  };
  house?: string;
  classes?: TeacherClassInfo[];
}

export interface NotificationPreference {
  onDeck?: boolean;
  push?: boolean;
  email?: boolean;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  profile?: string;
  isWebAccessAllowed?: boolean;
  family?: {
    relation: string;
    children: [User];
  };
  enrollmentNo?: string;
  courseInfo?: [CourseInfo];
  school: string;
  status?: UserStatus;
  gender: Gender;
  schools?: string[];
  stopId?: string[];
  rollOverInfo?: RollOverInfo[];
  personalDetails?: PersonalInformation;
  emergencyContactDetails?: EmergencyContactDetails;
  officialDetails?: OfficialDetails;
  occupationDetails?: OccupationDetails;
  studentHealthInfo?: StudentHealthInfo;
  medicalPractitionerInfo?: MedicalPractitionerInfo;
  guardianInfo?: GuardianInfo;
  notificationPreference?: NotificationPreference;
  displayRole?: string;
  parents?: User[];
  customFields?: any[]; // Add customFields
  siblings?: User[]; // Add siblings
}

export type StudentAuthorizedPickupPerson = {
  id: string;
  studentId: string;
  authorizedPersonName: string;
  contactNumber: string;
  relation: string;
  profile: string;
  authorizedFrom: Date;
  authorizedTill: Date;
  isActive: boolean;
};

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  IN_ACTIVE = 'IN_ACTIVE'
}

export interface EnsureUserDetails {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  profile: string;
  family: FamilyInfo;
  courseInfo: [CourseInfo];
  school: string;
  gender: Gender;
  schools?: string[];
  enrollmentNo?: string;
  parents: User[];
  otherAuthorizedPersons: StudentAuthorizedPickupPerson[];
}

export type FamilyInfo = {
  relation: string;
  children: User[];
};

export type AuthorizedPersonForEnsure = {
  name: string;
  relation: string;
  imageUrl: string;
  authorizedTill: string;
  contactNo?: string;
};
export type PersonalInformation = {
  contactNo?: string;
  nationality?: string;
  country?: string;
  residentialAddress?: string;
  birthPlace?: string;
  religion?: string;
  caste?: string;
  motherTongue?: string;
  dateOfBirth?: Date;
  bloodGroup?: string;
};

export type EmergencyContactDetails = {
  emergencyContactPersonName?: string;
  relationWithStudent?: string;
  emergencyContactNo?: string;
};

export type OfficialDetails = {
  passportNo?: string;
  passportExpiryDate?: Date;
  visa?: string;
  adhaarCardNo?: string;
  panCardNo?: string;
};

export type OccupationDetails = {
  occupation?: string;
  profession?: string;
  organizationName?: string;
  designation?: string;
  officeContactNo?: string;
  officeAddress?: string;
  annualIncome?: string;
  isGovermentEmployee?: string;
  department?: string;
  websiteOrLinkedInProfileUrl?: string;
};

export type StudentHealthInfo = {
  isChildTakingAnyMedication?: string;
  medicationDetails?: string;
  doesChildHaveAnyAllergies?: string;
  allergyDetails?: string;
  anyMedicalProblem?: string;
  medicalProblemDetails?: string;
};

export type MedicalPractitionerInfo = {
  name?: string;
  address?: string;
  clinicPhoneNo?: string;
  mobileNo?: string;
};

export type GuardianInfo = {
  name?: string;
  relation?: string;
  emailId?: string;
  personalDetails?: PersonalInformation;
  officialDetails?: OfficialDetails;
  educationalQualification?: string;
  occupationDetails?: OccupationDetails;
  profile: string;
};

export type RollOverInfo = {
  academicYear: string;
  grade: string;
  isGoingToRollOver: boolean;
  rollOverSubmittedDate?: Date;
  studentDetailsSubmittedDate?: Date;
  outstandingFeesPaymentLink?: string;
  isNewAdmission?: boolean;
  esign?: string;
  division?: string;
  concessionsWaiversRemark?: string; // Added this line
};

export interface UserDetailedView {
  id?: string;
  _id?: string;
  email: string;
  enrollmentNo?: string;
  profile?: string;
  name: string;
  school: string;
  role: UserRole;
  gender: Gender;
  courseInfo?: CourseInfo[];
  family?: FamilyInfo;
  parents?: User[];
  schools?: string[];
  curriculum?: string;
  category?: string;
  status?: UserStatus;
  educationalQualification?: string;
  personalDetails?: PersonalInformation;
  emergencyContactDetails?: EmergencyContactDetails;
  officialDetails?: OfficialDetails;
  occupationDetails?: OccupationDetails;
  studentHealthInfo?: StudentHealthInfo;
  medicalPractitionerInfo?: MedicalPractitionerInfo;
  guardianInfo?: GuardianInfo;
  rollOverInfo?: RollOverInfo[];
}

export type RolloverUserParents = {
  father: UserDetailedView;
  mother: UserDetailedView;
};

export type RolloverUserDetails = {
  parents: RolloverUserParents;
  students: User[];
};

export interface TransformedStudentDetail {
  studentDetails: User & {
    currentClass: string;
    passportExpiryDate?: string;
    customFieldsMap: Record<string, any>;
    currentCourseInfo?: CourseInfo;
    academicYear: string;
    isNewAdmission: boolean;
    dateOfJoining?: string;
  };
  fatherDetails: User;
  motherDetails: User;
  siblingDetails: {
    status?: UserStatus;
    enrollmentNo?: string;
    name: string;
    class?: string;
  }[];
}

export interface PaymentUser {
  email: string;
  role: AdminUserRoles;
  school: string;
  schools: string[];
  id: string;
}
